import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  UserIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  PlusCircleIcon,
  BriefcaseIcon,
  CreditCardIcon,
  BellIcon,
  ChatBubbleLeftRightIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../../store/authStore';
import { SidebarSection } from '../../../types';

function buildSidebarSections(isAgency: boolean): SidebarSection[] {
  const accountItems = [
    { labelKey: 'myProfile.brandSidebar.myInformation', path: '/host/info', icon: UserIcon },
    { labelKey: 'myProfile.brandSidebar.businessProfile', path: '/host/business-profile', icon: BuildingOfficeIcon },
  ];

  // Agency-only: client brands menu after business profile
  if (isAgency) {
    accountItems.push({
      labelKey: 'myProfile.brandSidebar.clientBrands', path: '/host/client-brands', icon: UserGroupIcon,
    });
  }

  return [
    { titleKey: 'myProfile.brandSidebar.accountManagement', items: accountItems },
    {
      titleKey: 'myProfile.brandSidebar.activityManagement',
      items: [
        { labelKey: 'myProfile.brandSidebar.registerCampaign', path: '/campaigns/register', icon: PlusCircleIcon },
        { labelKey: 'myProfile.brandSidebar.myCampaigns', path: '/campaigns', icon: BriefcaseIcon },
        { labelKey: 'myProfile.brandSidebar.paymentInfo', path: '/host/payment', icon: CreditCardIcon },
      ],
    },
    {
      titleKey: 'myProfile.brandSidebar.customerService',
      items: [
        { labelKey: 'myProfile.brandSidebar.notice', path: '/notice', icon: BellIcon },
        { labelKey: 'myProfile.brandSidebar.inquiry', path: '/inquiry', icon: ChatBubbleLeftRightIcon },
        { labelKey: 'myProfile.brandSidebar.settings', path: '/settings', icon: Cog6ToothIcon },
      ],
    },
  ];
}

export interface HostSidebarProps {
  variant?: 'standalone' | 'embedded';
  onItemClick?: () => void;
}

export const HostSidebar: React.FC<HostSidebarProps> = ({
  variant = 'standalone',
  onItemClick,
}) => {
  const { t } = useTranslation('profile');
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuthStore();

  const isAgency = user?.userType === 'agency';
  const sections = buildSidebarSections(isAgency);

  const isActive = (path: string) => location.pathname === path;

  const handleNavigate = (path: string) => {
    navigate(path);
    onItemClick?.();
  };

  const handleLogout = async () => {
    if (!window.confirm(t('myProfile.sidebar.logoutConfirm', '로그아웃 하시겠습니까?'))) return;
    await logout();
    navigate('/login');
  };

  // Embedded variant: just nav items for Layout sidebar
  if (variant === 'embedded') {
    return (
      <>
        {sections.map((section, idx) => (
          <div key={section.titleKey} className={idx > 0 ? 'mt-6' : ''}>
            <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              {t(section.titleKey)}
            </h3>
            <ul className="space-y-1">
              {section.items.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <li key={item.path}>
                    <button
                      onClick={() => handleNavigate(item.path)}
                      className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        active ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className={`h-5 w-5 mr-3 flex-shrink-0 ${active ? 'text-blue-600' : 'text-gray-400'}`} />
                      {t(item.labelKey)}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        <div className="mt-1">
          <ul><li>
            <button onClick={handleLogout} className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-100 transition-colors">
              <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3 flex-shrink-0 text-gray-400" />
              {t('myProfile.sidebar.logout')}
            </button>
          </li></ul>
        </div>
      </>
    );
  }

  return null; // standalone not needed for now
};
